import type { Client, PaymentMethod, Product, Quote } from '@/types/database';
import { isVariant, variantDisplayName } from '@/lib/variants';
import { productUnitCost } from '@/lib/cost';
import { roundMoney } from '@/lib/pricing';
import { roundQuantity } from '@/lib/quantity';
import { quoteItemLineTotal } from '@/lib/quoteTotals';

export const QUICK_SALE_TITLE = 'Venda rápida';
export const WALK_IN_CUSTOMER_NAME = 'Consumidor não identificado';

export interface QuickSaleCartItem {
  id: string;
  product_id: string;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  /**
   * SNAPSHOT do custo do produto no instante em que ele entrou no carrinho.
   * Congelado de propósito: o PDV grava `quote_items.unit_cost` a partir daqui,
   * então mudar o custo no catálogo depois não reescreve a venda já fechada.
   */
  unit_cost: number | null;
  discount: number;
  item_type: 'service' | 'product';
  unit: string | null;
  image_url: string | null;
  barcode: string | null;
  track_stock: boolean;
  stock_quantity: number;
  /**
   * Serviço-kit que trouxe esta linha ao carrinho (grupo 10 / B2). `null` = o
   * operador escolheu o item diretamente.
   *
   * ─── POR QUE O PDV EXPANDE, DEPOIS DE TRÊS GRUPOS FORA ────────────────────
   *
   * A objeção original continua correta: o balcão baixa estoque AUTOMATICAMENTE
   * e sem perguntar, então expandir um kit escondido faria a venda de um
   * serviço subtrair materiais que o operador nunca viu. A saída não foi mudar
   * a baixa, foi tornar o kit VISÍVEL: cada material é uma linha do carrinho,
   * com a mesma projeção de saldo das outras, e pode ser removido antes de
   * finalizar. O que o operador confirma passa a ser exatamente o que sai do
   * estoque — que era o requisito real por trás da objeção.
   */
  bundle_parent_id: string | null;
  /**
   * Quantidade do material POR UNIDADE do serviço-kit. É o que permite
   * reescalar quando o operador muda a quantidade do serviço no carrinho:
   * vender 3 instalações que consomem 2 buchas cada tem que baixar 6 buchas, e
   * um carrinho que não reescala baixaria 2 — errado, e em silêncio.
   */
  bundle_unit_quantity?: number;
}

export interface QuickSaleOrderInput {
  userId: string;
  companyId: string;
  cart: QuickSaleCartItem[];
  client?: Client | null;
}

export interface QuickSaleOrderPayload {
  quote: {
    user_id: string;
    company_id: string;
    client_id: string | null;
    client_name: string;
    client_phone: string | null;
    client_address: string | null;
    client_document: string | null;
    status: 'completed';
    document_type: 'pedido';
    validity_days: null;
    warranty: null;
    subtitle: null;
    issue_place: null;
    extra_fields: null;
    notes: null;
    terms_conditions: null;
    total: number;
    /**
     * O PDV NÃO tem frete (venda de balcão: o cliente leva na hora) nem desconto
     * de cabeçalho — o desconto do balcão é dado por item, no carrinho. As
     * colunas vão explicitamente zeradas em vez de omitidas para que o payload
     * seja auto-explicativo e o guard de varredura não precise de exceção.
     */
    shipping: 0;
    discount: 0;
    discount_type: 'value';
    /**
     * Campos de exibição do grupo 4, explicitamente NULOS.
     *
     * `payment_methods` do orçamento são as formas ACEITAS; a venda de balcão já
     * está paga e a forma efetivamente USADA vai para
     * `finance_entries.payment_method` (singular) — semânticas diferentes, não
     * unifique. `null` aqui é a leitura "não informado", que mantém o documento
     * do PDV exatamente como sempre foi (inclusive com o QR PIX, se a empresa
     * tem chave). Vão explícitos, e não omitidos, pelo mesmo motivo do frete
     * acima: payload auto-explicativo e sem exceção no guard de varredura.
     */
    payment_methods: null;
    payment_terms: null;
    reference: null;
    /** PDV não tem seletor de modelo: o PDF segue o padrão da empresa. */
    pdf_template: null;
    title: string;
    sequence_id: null;
    show_sequence_id: false;
  };
  items: Array<{
    product_id: string;
    name: string;
    description: string | null;
    quantity: number;
    unit_price: number;
    unit_cost: number | null;
    discount: number;
    discount_type: 'value';
    item_type: 'service' | 'product';
    unit: string | null;
    image_url: string | null;
    barcode: string | null;
    total: number;
    position: number;
    finance_entry_id: null;
  }>;
  /**
   * Venda Rápida é uma venda consumada, não uma proposta. Esta flag atravessa
   * a fila offline para o worker não incrementar a cota ao sincronizar.
   */
  countTowardQuoteQuota: false;
}

export interface QuickSaleStockLine {
  productId: string;
  name: string;
  quantity: number;
  projectedBalance: number;
}

export interface QuickSaleFinanceInput {
  userId: string;
  companyId: string;
  quoteId: string;
  clientId?: string | null;
  amount: number;
  paymentMethod: PaymentMethod;
  paidDate: string;
}

/**
 * O PDV grava na MESMA coluna `quote_items.total` que o orçamento, então usa a
 * mesma fórmula. O desconto do carrinho é sempre em VALOR (não há % no balcão),
 * que é o comportamento padrão de `quoteItemLineTotal`.
 */
export function quickSaleLineTotal(item: Pick<QuickSaleCartItem, 'quantity' | 'unit_price' | 'discount'>): number {
  return quoteItemLineTotal({ ...item, discount_type: 'value' });
}

export function quickSaleTotal(cart: QuickSaleCartItem[]): number {
  return roundMoney((cart ?? []).reduce((sum, item) => sum + quickSaleLineTotal(item), 0));
}

export function canFinalizeQuickSale(cart: QuickSaleCartItem[]): boolean {
  return Array.isArray(cart) && cart.length > 0;
}

function displayNameForProduct(product: Product, allProducts: Product[]): string {
  if (!isVariant(product)) return product.name;
  const parent = allProducts.find((candidate) => candidate.id === product.parent_product_id);
  return variantDisplayName(parent?.name ?? product.name, product.variant_name);
}

export function toQuickSaleCartItem(product: Product, allProducts: Product[] = [product]): QuickSaleCartItem {
  return {
    id: product.id,
    product_id: product.id,
    name: displayNameForProduct(product, allProducts),
    description: product.description ?? null,
    quantity: 1,
    unit_price: roundMoney(Number(product.price ?? 0)),
    // Snapshot: lido UMA vez, aqui. `null` quando o produto não tem custo
    // informado — nunca 0, que significaria "de graça".
    unit_cost: productUnitCost(product),
    discount: 0,
    item_type: product.type === 'product' ? 'product' : 'service',
    unit: product.unit ?? null,
    image_url: product.image_url ?? null,
    barcode: product.barcode ?? null,
    track_stock: product.type === 'product' && product.track_stock === true,
    stock_quantity: Number(product.stock_quantity ?? 0),
    bundle_parent_id: null,
  };
}

export function addQuickSaleItem(
  cart: QuickSaleCartItem[],
  product: Product,
  quantity = 1,
  allProducts: Product[] = [product],
): QuickSaleCartItem[] {
  const increment = roundQuantity(quantity);
  if (!(increment > 0)) return cart;

  const existing = cart.find((item) => item.product_id === product.id);
  if (existing) {
    return cart.map((item) =>
      item.product_id === product.id
        ? { ...item, quantity: roundQuantity(item.quantity + increment) }
        : item,
    );
  }

  return [...cart, { ...toQuickSaleCartItem(product, allProducts), quantity: increment }];
}

/**
 * Remove o item. Se ele for um serviço-kit, os materiais que ELE trouxe saem
 * junto: deixá-los para trás produziria linhas órfãs rotuladas "incluso em
 * <serviço que não está mais no carrinho>" e, pior, baixaria material de um
 * serviço que não está sendo vendido. Material adicionado à mão pelo operador
 * não tem origem e não é tocado.
 */
export function removeQuickSaleItem(cart: QuickSaleCartItem[], productId: string): QuickSaleCartItem[] {
  return cart.filter(
    (item) => item.product_id !== productId && item.bundle_parent_id !== productId,
  );
}

/**
 * Muda a quantidade e REESCALA os materiais daquele kit.
 *
 * Sem o reescalonamento, mudar de 1 para 3 instalações venderia 3 serviços e
 * continuaria baixando material para 1 — estoque errado, sem erro na tela. É o
 * mesmo motivo pelo qual `bundleLinesForQuote` multiplica pela quantidade do
 * serviço no orçamento.
 */
export function setQuickSaleQuantity(
  cart: QuickSaleCartItem[],
  productId: string,
  quantity: number,
): QuickSaleCartItem[] {
  const next = roundQuantity(quantity);
  if (!(next > 0)) return cart;
  return cart.map((item) => {
    if (item.product_id === productId) {
      // Se o operador digita a quantidade de um MATERIAL, o número dele passa
      // a valer: a linha para de ser reescalada pelo serviço (mas continua
      // rotulada e continua saindo junto se o serviço for removido). Sem isto,
      // mexer no serviço depois apagaria o ajuste manual sem aviso.
      const { bundle_unit_quantity: _autoScale, ...rest } = item;
      return item.bundle_parent_id ? { ...rest, quantity: next } : { ...item, quantity: next };
    }
    if (item.bundle_parent_id === productId && item.bundle_unit_quantity != null) {
      return { ...item, quantity: roundQuantity(item.bundle_unit_quantity * next) };
    }
    return item;
  });
}

/**
 * Acrescenta ao carrinho os materiais de um serviço-kit.
 *
 * `resolved` vem de `resolveBundleItems` (src/lib/productBundles.ts). A
 * quantidade é `quantidade do kit × quantidade do serviço`, igual ao
 * orçamento. Um material já presente NÃO é somado de novo por este caminho: a
 * linha do kit é reescalada por `setQuickSaleQuantity`, e somar aqui contaria
 * duas vezes o mesmo material a cada nova leitura do código de barras.
 */
export function addQuickSaleBundleItems(
  cart: QuickSaleCartItem[],
  materials: { product: Product; quantity: number; displayName: string }[],
  opts: { parentId: string; parentQuantity?: number; allProducts?: Product[] },
): QuickSaleCartItem[] {
  const parentQty = Number(opts.parentQuantity ?? 1);
  const multiplier = Number.isFinite(parentQty) && parentQty > 0 ? parentQty : 1;
  let next = cart;

  for (const material of materials ?? []) {
    const productId = String(material.product.id);
    if (next.some((item) => item.product_id === productId)) continue;

    const base = toQuickSaleCartItem(material.product, opts.allProducts ?? [material.product]);
    next = [
      ...next,
      {
        ...base,
        name: material.displayName || base.name,
        quantity: roundQuantity(material.quantity * multiplier),
        bundle_parent_id: String(opts.parentId),
        bundle_unit_quantity: material.quantity,
      },
    ];
  }

  return next;
}

export function buildQuickSaleOrderPayload(input: QuickSaleOrderInput): QuickSaleOrderPayload {
  const client = input.client ?? null;
  const total = quickSaleTotal(input.cart);

  return {
    quote: {
      user_id: input.userId,
      company_id: input.companyId,
      client_id: client?.id ?? null,
      client_name: client?.name?.trim() || WALK_IN_CUSTOMER_NAME,
      client_phone: client?.phone ?? null,
      client_address: client?.address ?? null,
      client_document: client?.document ?? null,
      status: 'completed',
      document_type: 'pedido',
      validity_days: null,
      warranty: null,
      // O balcao nao tem cabecalho editavel (nem subtitulo, nem local de
      // emissao): `null` explicito, pelo mesmo motivo de `pdf_template`.
      subtitle: null,
      issue_place: null,
      extra_fields: null,
      notes: null,
      terms_conditions: null,
      total,
      shipping: 0,
      discount: 0,
      discount_type: 'value',
      payment_methods: null,
      payment_terms: null,
      reference: null,
      // PDV não tem seletor de modelo: null = o PDF segue o padrão da empresa.
      pdf_template: null,
      title: QUICK_SALE_TITLE,
      sequence_id: null,
      show_sequence_id: false,
    },
    items: input.cart.map((item, position) => ({
      product_id: item.product_id,
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      // Vem do carrinho, NÃO de uma leitura nova do catálogo.
      unit_cost: item.unit_cost,
      discount: item.discount,
      discount_type: 'value',
      item_type: item.item_type,
      unit: item.unit,
      image_url: item.image_url,
      barcode: item.barcode,
      total: quickSaleLineTotal(item),
      position,
      // O PDV não tem despesa cobrável: a venda de balcão é fechada na hora e
      // não passa por um orçamento que alguém edite depois.
      finance_entry_id: null,
    })),
    countTowardQuoteQuota: false,
  };
}

export function shouldIncrementQuoteQuota(data: {
  countTowardQuoteQuota?: boolean;
  document_type?: string | null;
  quote?: { document_type?: string | null };
}): boolean {
  // Compatibilidade: operações existentes/antigas não carregam a flag e
  // continuam contando normalmente. `pedido` fica como segunda defesa para uma
  // operação de PDV antiga/malformada jamais voltar a consumir cota.
  const documentType = data.quote?.document_type ?? data.document_type;
  return data.countTowardQuoteQuota !== false && documentType !== 'pedido';
}

export function buildQuickSaleStockLines(cart: QuickSaleCartItem[]): QuickSaleStockLine[] {
  return cart
    .filter((item) => item.track_stock && item.quantity > 0)
    .map((item) => ({
      productId: item.product_id,
      name: item.name,
      quantity: item.quantity,
      projectedBalance: roundQuantity(item.stock_quantity - item.quantity),
    }));
}

export function buildQuickSaleFinanceEntry(input: QuickSaleFinanceInput) {
  return {
    user_id: input.userId,
    company_id: input.companyId,
    quote_id: input.quoteId,
    client_id: input.clientId ?? null,
    category_id: null,
    recurrence_parent_id: null,
    kind: 'income' as const,
    description: 'Recebimento — Venda rápida',
    amount: roundMoney(input.amount),
    due_date: input.paidDate,
    paid_date: input.paidDate,
    status: 'paid' as const,
    payment_method: input.paymentMethod,
    recurrence: null,
    notes: null,
    // O lançamento do PDV é uma ENTRADA (o recebimento da venda), então nunca é
    // despesa cobrável — o CHECK do banco recusaria `billable` numa entrada.
    // Explícito, e não omitido, pela mesma razão dos campos acima: o guard de
    // varredura cobra a coluna em todo payload de `finance_entries`.
    billable: false,
    attachment_url: null,
  };
}

export function sellableQuickSaleProducts(products: Product[]): Product[] {
  const parentIds = new Set(
    products
      .filter((product) => Boolean(product.parent_product_id))
      .map((product) => String(product.parent_product_id)),
  );
  return products.filter((product) => !parentIds.has(String(product.id)));
}

export function findQuickSaleProductByBarcode(products: Product[], rawCode: string): Product | undefined {
  const code = rawCode.trim();
  if (!code) return undefined;
  const match = sellableQuickSaleProducts(products).find(
    (product) =>
      product.barcode === code ||
      (typeof product.description === 'string' && product.description.includes(code)),
  );
  return match ? { ...match, name: displayNameForProduct(match, products) } : undefined;
}

export function searchQuickSaleProducts(products: Product[], rawTerm: string): Product[] {
  const term = rawTerm.trim().toLocaleLowerCase('pt-BR');
  if (!term) return [];
  return sellableQuickSaleProducts(products)
    .map((product) => ({ ...product, name: displayNameForProduct(product, products) }))
    .filter((product) => {
      const haystack = [product.name, product.barcode, product.description]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('pt-BR');
      return haystack.includes(term);
    })
    .slice(0, 8);
}

/**
 * A linha é uma venda de balcão (PDV)?
 *
 * O parâmetro é declarado ESTRUTURALMENTE (`string | null`, não `DocumentType`)
 * para também aceitar as linhas cruas de `quotes` que vêm do PostgREST e do
 * cache offline tipadas como `string | null` — é justamente delas que os
 * relatórios precisam. Um `Pick<Quote, 'document_type'>` continua satisfazendo.
 */
export function isSaleDocument(quote: { document_type?: string | null }): boolean {
  return quote.document_type === 'pedido';
}
